import { FC, useState, useEffect, useMemo } from 'react';

import {
  InspectionAdminSetupForm,
  InspectionIssueResponseForm,
  InspectorInspectionForm,
  IssueReviewForm
} from './forms'
import { Application } from '../../../core/applications';
import { findIndex } from 'lodash';
import { VerdictButton } from '../../../../components/widgets';
import {  isValidString } from '../../../../components/helpers';
import { InspectorSamplingForm } from './forms/InspectorSamplingForm';

type Props = {
  appData: Application
  draftVars: Record<string, any>  
  reviewId: number
  reviewState: string  
  reviewDraftVars: Record<string, any>  
  doClose: Function    
}

const SubmitInspectionForm: FC<Props> = ({appData, draftVars, reviewState, reviewId, reviewDraftVars, doClose}) => {
    
  const [ verdict, setVerdict ] = useState<string | null>(null);
  
  const appState = appData?.state ?? '';

  const { isInspectionSampling, isRescheduleSampling, isPendingAdminReport } = useMemo(() => {
    let isInspectionSampling = false;
    let isRescheduleSampling = false;
    let isPendingAdminReport = false;
    
    if (appData && reviewId && appData?.applicationReviews) {
      let _idx = findIndex(appData.applicationReviews, { id: reviewId});
      if (_idx > -1) {
        isInspectionSampling = appData.applicationReviews[_idx].state === 'Inspection - Sampling';
        isRescheduleSampling = appData.applicationReviews[_idx].state === 'Inspection - Reschedule Sampling';
        isPendingAdminReport = appData.applicationReviews[_idx].state === 'Inspection - Pending Sampling Report';
      }
    }

    return { isInspectionSampling, isRescheduleSampling, isPendingAdminReport }
  }, [appData, reviewId]);
  
  useEffect(() => {

    if (! reviewId) {
      if (isValidString(draftVars?.verdict)) {
        setVerdict(draftVars.verdict);
      }  
      return;
    }

    if (isRescheduleSampling) {
      setVerdict('reskedSampling');
      return;
    }
    if (isPendingAdminReport) {
      setVerdict('adminReport');
      return;
    }

    if (isValidString(reviewDraftVars?.verdict)) {
      setVerdict(reviewDraftVars.verdict);
    }
    
  }, [isRescheduleSampling, isPendingAdminReport, draftVars, reviewDraftVars, reviewId])

  if (! (appData && appState)) {
    return null;
  }

  return(
  <div id='kt_submit_inspection_form' className='form'>
    <div
      className='d-flex flex-column scroll-y me-n7 pe-7'
      id='kt_submit_inspection_scroll'
      data-kt-scroll='true'
      data-kt-scroll-activate='{default: false, lg: true}'
      data-kt-scroll-max-height='auto'
      data-kt-scroll-dependencies='#kt_submit_inspection_header'
      data-kt-scroll-wrappers='#kt_submit_inspection_scroll'
      data-kt-scroll-offset='300px'
    >    
      { (!(appState === 'Inspection Process - Admin Setup' || reviewState === 'Issue - CoC' || isRescheduleSampling || isPendingAdminReport)) && 
      <>
      <div className="text-muted fw-normal fs-6 mb-2">Select verdict:</div>      
      <div className='row mb-2 gx-1 align-content-stretch'>            
        <VerdictButton 
          verdict={verdict}
          value="approved"
          desc="APPROVE"
          iconPath='/media/icons/duotune/abstract/abs009.svg'
          offClass='btn-outline btn-outline-dashed btn-outline-default text-hover-success'
          onClass='btn-success text-white'          
          handleSelect={() => { setVerdict('approved')}}
        />
        <VerdictButton 
          verdict={verdict}
          value="rejected"
          desc="REJECT"
          iconPath='/media/icons/duotune/abstract/abs012.svg'
          offClass='btn-outline btn-outline-dashed btn-outline-default text-hover-danger'
          onClass='btn-danger'       
          handleSelect={() => { setVerdict('rejected')}}
        />
      </div>
      {isInspectionSampling && 
      <div className='row mb-2 gx-1 align-items-stretch'>            
        <VerdictButton 
          verdict={verdict}
          value="pendingApplicant"
          desc="PENDING (Applicant)"          
          iconPath='/media/icons/duotune/general/gen049.svg'  
          offClass='btn-outline btn-outline-dashed btn-outline-default text-hover-primary'
          onClass='btn-primary text-white'          
          handleSelect={() => { setVerdict('pendingApplicant')}}
          title="Pending Report - Assign to Applicant"
          size={4}
        />        
        <VerdictButton 
          verdict={verdict}
          value="pendingAdmin"
          desc="PENDING (Admin)"          
          iconPath='/media/icons/duotune/general/gen049.svg'  
          offClass='btn-outline btn-outline-dashed btn-outline-default text-hover-primary'
          onClass='btn-primary text-white'          
          handleSelect={() => { setVerdict('pendingAdmin')}}
          title="Pending Report - Assign to Admin"
          size={4}
        />        
        <VerdictButton 
          verdict={verdict}
          value="reschedule"
          desc="RESCHEDULE"          
          iconPath='/media/icons/duotune/general/gen014.svg'  
          offClass='btn-outline btn-outline-dashed btn-outline-default text-hover-primary'
          onClass='btn-primary text-white'          
          handleSelect={() => { setVerdict('reschedule')}}
          size={4}          
        />        
      </div>
      }
      
      { verdict && 
      <div className='separator border-gray-200 my-4'></div>                  
      }
      </>
      }
      { (appState === 'Inspection Process - Admin Setup') &&  
      <InspectionAdminSetupForm appData={appData} draftVars={draftVars} doClose={doClose} />             
      }

      { (verdict && appState === 'Inspection Process - Inspection' && reviewId && (reviewState === 'Inspection - Sampling')) && 
      <InspectorSamplingForm 
        appData={appData}                    
        reviewId={reviewId}                    
        reviewState={reviewState}
        draftVars={reviewDraftVars}                             
        verdict={verdict} 
        doClose={doClose}                     
      /> 
      }      
      { (verdict && appState === 'Inspection Process - Inspection' && reviewId && (! ['Issue - CoC','Inspection - Sampling'].includes(reviewState))) && 
      <InspectorInspectionForm 
        appData={appData}                    
        reviewId={reviewId}      
        reviewState={reviewState}              
        draftVars={reviewDraftVars}                     
        verdict={verdict} 
        doClose={doClose}                     
      /> 
      }
      { (verdict && appState.match(/^Inspection Process(\s*)-(\s*)Issue Review/i)) && 
      <IssueReviewForm 
        appData={appData}         
        draftVars={draftVars}                     
        verdict={verdict} 
        doClose={doClose}                     
      />
      }
      { (reviewState === 'Issue - CoC') &&  
      <InspectionIssueResponseForm reviewId={reviewId} draftVars={reviewDraftVars} doClose={doClose} />             
      }
    </div>
  </div>    
  )      
}

export {SubmitInspectionForm}
